<template>
  <div class="flex flex-col gap-6">
    <!-- Page Header -->
    <div class="flex items-center justify-between">
      <div>
        <h1 class="text-2xl font-bold text-slate-800">流程管理</h1>
        <p class="text-sm text-slate-500 mt-1">查看和管理项目的业务流程流转进度</p>
      </div>
      <div class="flex items-center gap-3">
        <span class="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold">项目管理</span>
      </div>
    </div>

    <!-- Left Side Menu + Content Layout -->
    <div class="flex gap-6">
      <!-- Left Side Menu -->
      <div class="w-[200px] flex-shrink-0">
        <div class="glass-card rounded-2xl p-3 space-y-2">
          <router-link 
            to="/project-list" 
            class="w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 text-left flex items-center gap-3"
            :class="$route.path === '/project-list' ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' : 'text-slate-600 hover:bg-slate-100'"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
            </svg>
            项目列表
          </router-link>
          <router-link 
            to="/process-management" 
            class="w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-300 text-left flex items-center gap-3"
            :class="$route.path === '/process-management' ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25' : 'text-slate-600 hover:bg-slate-100'"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
            </svg>
            流程管理
          </router-link>
        </div>
      </div>

      <!-- Main Content -->
      <div class="flex-1">
        <!-- Workflow Progress Bar -->
        <div class="glass-card rounded-2xl p-6 mb-6">
          <div class="flex items-center justify-between">
            <div 
              v-for="(step, index) in workflowSteps" 
              :key="step.key"
              class="flex items-center flex-1 cursor-pointer group"
              @click="selectStep(index)"
            >
              <div class="flex flex-col items-center relative">
                <div 
                  class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300"
                  :class="[
                    currentStep === index ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/30 scale-110' :
                    index < currentStep ? 'bg-blue-500 text-white' :
                    'bg-slate-200 text-slate-500 hover:bg-slate-300'
                  ]"
                >
                  <svg v-if="index < currentStep" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span v-else>{{ index + 1 }}</span>
                </div>
                <span 
                  class="mt-2 text-xs font-medium transition-colors duration-300 absolute top-full whitespace-nowrap"
                  :class="currentStep === index ? 'text-blue-600 font-bold' : (index < currentStep ? 'text-blue-500' : 'text-slate-400')"
                >
                  {{ step.label }}
                </span>
              </div>
              <div v-if="index < workflowSteps.length - 1" 
                   class="flex-1 h-0.5 mx-2 transition-colors duration-300" 
                   :class="index < currentStep ? 'bg-blue-500' : 'bg-slate-200'"></div>
            </div>
          </div>
          <div class="mt-8 text-center">
            <span class="text-sm text-slate-500">当前筛选: </span>
            <span class="text-sm font-bold text-blue-600">{{ workflowSteps[currentStep].label }}</span>
            <span class="text-sm text-slate-400 ml-2">({{ filteredProjects.length }} 个项目)</span>
          </div>
        </div>

        <!-- Search and Filter -->
        <div class="flex items-center gap-4 mb-6">
          <div class="flex-1 relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="搜索项目编号或项目名称..."
              class="premium-input w-full rounded-xl pl-10 pr-4 py-3 text-sm"
            />
            <svg class="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <select v-model="filterStatus" class="premium-input rounded-xl px-4 py-3 text-sm min-w-[140px]">
            <option value="">全部状态</option>
            <option value="completed">已完成</option>
            <option value="inProgress">进行中</option>
            <option value="notStarted">未开始</option>
          </select>
        </div>

        <!-- Data Table -->
        <div class="glass-card rounded-3xl overflow-hidden">
          <div class="overflow-x-auto">
            <table class="w-full">
              <thead>
                <tr class="bg-slate-50/80 border-b border-slate-200">
                  <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">项目编号</th>
                  <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">项目名称</th>
                  <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">负责人</th>
                  <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">状态</th>
                  <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">当前环节</th>
                  <th class="px-6 py-4 text-left text-xs font-bold text-slate-600 uppercase tracking-wider">操作</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                <tr v-for="(project, index) in filteredProjects" :key="project.id" class="hover:bg-slate-50/50 transition-colors">
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm font-medium text-slate-700">{{ project.code }}</span>
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex items-center gap-3">
                      <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold text-xs">
                        {{ project.name.charAt(0) }}
                      </div>
                      <button 
                        @click="goToProjectDetail(project.id)"
                        class="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline text-left"
                      >
                        {{ project.name }}
                      </button>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm text-slate-600">{{ project.leader }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="px-3 py-1 rounded-full text-xs font-medium"
                      :class="{
                        'bg-emerald-50 text-emerald-600': project.status === 'completed',
                        'bg-blue-50 text-blue-600': project.status === 'inProgress',
                        'bg-slate-100 text-slate-500': project.status === 'notStarted'
                      }"
                    >
                      {{ statusText(project.status) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span class="text-sm text-slate-600">{{ project.currentStage }}</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-3">
                      <button 
                        @click="goToProjectDetail(project.id)"
                        class="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                      >
                        查看详情
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Empty State -->
          <div v-if="filteredProjects.length === 0" class="py-12 text-center">
            <svg class="w-16 h-16 text-slate-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
            </svg>
            <p class="text-slate-500">该流程节点下暂无项目</p>
          </div>

          <!-- Pagination -->
          <div v-if="filteredProjects.length > 0" class="flex items-center justify-between px-6 py-4 border-t border-slate-200">
            <span class="text-sm text-slate-500">共 {{ filteredProjects.length }} 条记录</span>
            <div class="flex items-center gap-2">
              <button class="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50" disabled>上一页</button>
              <button class="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-sm font-medium">1</button>
              <button class="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">2</button>
              <button class="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">3</button>
              <button class="px-3 py-1.5 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">下一页</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getAllProjects } from '../services/indexedDB'
import { initDatabase } from '../utils/dbInitializer'

export default {
  name: 'ProcessManagement',
  data() {
    return {
      currentStep: 0,
      searchQuery: '',
      filterStatus: '',
      workflowSteps: [
        { key: 'declaration', label: '申报书审查' },
        { key: 'approval', label: '立项评审' },
        { key: 'task', label: '任务书审查' },
        { key: 'midterm', label: '中期审查' },
        { key: 'annual', label: '年度审查' },
        { key: 'final', label: '结题报告审查' }
      ],
      projects: [],
      loading: false
    }
  },
  computed: {
    filteredProjects() {
      // First filter by current step
      let result = this.projects.filter(p => p.currentStep === this.currentStep)

      // Then filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        result = result.filter(p =>
          p.code.toLowerCase().includes(query) ||
          p.name.toLowerCase().includes(query)
        )
      }

      // Finally filter by status
      if (this.filterStatus) {
        result = result.filter(p => p.status === this.filterStatus)
      }

      return result
    }
  },
  async created() {
    await this.loadProjects()
  },
  methods: {
    async loadProjects() {
      this.loading = true
      try {
        // 确保数据库已初始化
        await initDatabase()
        // 从 IndexedDB 加载项目数据
        this.projects = await getAllProjects()
      } catch (error) {
        console.error('加载项目数据失败:', error)
      } finally {
        this.loading = false
      }
    },
    statusText(status) {
      const map = {
        completed: '已完成',
        inProgress: '进行中',
        notStarted: '未开始'
      }
      return map[status] || status
    },
    selectStep(index) {
      this.currentStep = index
    },
    goToProjectDetail(projectId) {
      this.$router.push(`/project-detail/${projectId}`)
    }
  }
}
</script>
